import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import styled, { css } from 'styled-components';
import hooks from '@g4/automation-hooks';
import { PropTypes as G4PropTypes } from '@g4/lynx';

import oldTheme from './Colors';

const COLOR_LIGHTBLUE = 'lightblue';
const COLOR_BLUE = 'blue';
const COLOR_WHITE = 'white';
const COLOR_BLACK = 'black';

const VALIGN_TOP = 'top';
const VALIGN_BOTTOM = 'bottom';

const SIZE_DEFAULT = 'default';
const SIZE_MEDIUM = 'medium';
const SIZE_LARGE = 'large';

export const Currency = styled.span`
  font-size: 0.75em;

  ${props => {
    switch (props.color) {
      case COLOR_LIGHTBLUE:
        return css`
          color: ${oldTheme.colors.modern.price.lightBlue};
        `;
      case COLOR_BLUE:
        return css`
          color: ${oldTheme.colors.modern.price.blue};
        `;
      case COLOR_WHITE:
        return css`
          color: ${oldTheme.colors.modern.price.white};
        `;
      case COLOR_BLACK:
        return css`
          color: ${oldTheme.colors.modern.price.black};
        `;
      default:
        break;
    }
  }};
`;

export const Cents = styled.sup`
  font-size: 75%;
  line-height: normal;
  font-weight: normal;
`;

export const Dollars = styled.span`
  font-weight: bold;
`;

export const Sign = styled.span`
  font-weight: bold;
`;

export const Message = styled.span`
  ${props =>
    props.withSpace &&
    !props.noFormat &&
    css`
      margin-left: 0.2rem;
    `} ${props =>
    !props.noFormat &&
    css`
      font-size: 0.4167em;
    `};
`;

export const Amount = styled.span`
  white-space: nowrap;

  ${props =>
    props.noFormat &&
    css`
      line-height: 1.25rem;
    `}

  ${props =>
    props.size === SIZE_MEDIUM &&
    css`
      font-size: 1.2rem;
    `}

  ${props =>
    props.size === SIZE_LARGE &&
    css`
      font-size: 1.5rem;
    `};


`;

export const DiagonalDel = styled.del`
  text-decoration: none;
  vertical-align: top;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 50%;
    border-top: 1.5px solid;
    transform: rotate(-15deg);
  }
`;
export const Linethrough = styled.del`
  text-decoration: line-through;
`;

export class Price extends PureComponent {
  static propTypes = {
    /** Associates a specific action onto an element for the benefit of automated clients consuming it */
    automationHook: PropTypes.string,
    /** Amount to display */
    amount: PropTypes.number.isRequired,
    /** Class attribute */
    className: PropTypes.string,
    /** Determines whether to specially format the component */
    noFormat: PropTypes.bool,
    /** Determines whether to display cents or not */
    noCents: PropTypes.bool,
    /** Replace amount with corresponding string literal, if found in this object */
    literals: PropTypes.object,
    /** Vertical alignment of all child elements of this component */
    vAlign: PropTypes.oneOf([VALIGN_TOP, VALIGN_BOTTOM]),
    /** Specifies whether a space should be visible between dollars and cents */
    withSpace: PropTypes.bool,
    /** Message to display alongside the price. Inherits `withSpace` and `noFormat` */
    message: PropTypes.string,
    /** Determines how large the content is */
    size: PropTypes.oneOf([SIZE_DEFAULT, SIZE_MEDIUM, SIZE_LARGE]),
    /** Specify particular colors to use within the component */
    colors: G4PropTypes.colors([
      COLOR_LIGHTBLUE,
      COLOR_BLUE,
      COLOR_WHITE,
      COLOR_BLACK,
    ]),
    /** Determines whether to place a strikethrough / linethrough line through the component */
    strikethrough: PropTypes.bool,
    linethrough: PropTypes.bool,
  };

  static defaultProps = {
    amount: 0,
    noFormat: false,
    noCents: false,
    literals: null,
    withSpace: false,
    size: SIZE_DEFAULT,
    colors: {
      currency: undefined,
    },
    strikethrough: false,
    linethrough: false,
  };

  zeroPadRight(number, desiredLength) {
    number += ''; // Convert it to string

    if (number.length >= desiredLength) {
      return number;
    } else {
      return [number].concat(Array(desiredLength - number.length)).join('0');
    }
  }

  commafy(num) {
    var result = (num + '').split(''); // An array of digits

    for (let i = result.length - 3; i > 0; i -= 3) {
      result.splice(i, 0, ',');
    }

    return result.join('');
  }

  render() {
    const {
      literals,
      noFormat,
      noCents,
      amount,
      message,
      withSpace,
      strikethrough,
      linethrough,
      vAlign,
      size,
    } = this.props;

    const isNegative = amount < 0;
    const amountStr = amount + '';
    const integerPartStr = Math.abs(amountStr.split('.')[0]) + '';
    const decimalPartStr = amountStr.split('.')[1] || '';

    let renderFragment;

    if (literals && Object.prototype.hasOwnProperty.call(literals, amountStr)) {
      renderFragment = strikethrough ? (
        <DiagonalDel>
          <span>{literals[amountStr]}</span>
        </DiagonalDel>
      ) : linethrough ? (
        <Linethrough>
          <span>{literals[amountStr]}</span>
        </Linethrough>
      ) : (
        <span>{literals[amountStr]}</span>
      );
    } else {
      let amountNode;

      if (noFormat) {
        amountNode = (
          <Amount
            noFormat
            vAlign={vAlign}
            size={size}
            {...hooks.hook(this.props.automationHook)}
          >
            {isNegative && '-'}
            &#36;
            {this.commafy(integerPartStr)}
            {!noCents && '.'}
            {!noCents && this.zeroPadRight(decimalPartStr, 2)}
            {message && <Message noFormat>/{message}</Message>}
          </Amount>
        );
      } else {
        amountNode = (
          <Amount
            vAlign={vAlign}
            size={size}
            {...hooks.hook(this.props.automationHook)}
          >
            {isNegative && <Sign>-</Sign>}
            <Currency color={this.props.colors.currency}>&#36;</Currency>
            <Dollars>{this.commafy(integerPartStr)}</Dollars>
            {!noCents && <Cents>{this.zeroPadRight(decimalPartStr, 2)}</Cents>}
            {message && <Message withSpace={withSpace}>/{message}</Message>}
          </Amount>
        );
      }

      renderFragment = strikethrough ? (
        <DiagonalDel>{amountNode}</DiagonalDel>
      ) : linethrough ? (
        <Linethrough>{amountNode}</Linethrough>
      ) : (
        amountNode
      );
    }

    return <span className={this.props.className}>{renderFragment}</span>;
  }
}

export default styled(Price)`
  ${/* sc-selector */ Cents} {
    ${props =>
      props.vAlign === VALIGN_BOTTOM
        ? `
      vertical-align: baseline;
    `
        : `
      vertical-align: super;
      position: relative;
      top: 0;
    `}
  }
`;
